import React, { createContext, useContext, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  appModalCloseButtonSx, appModalHeaderDividerSx, appModalPaperSx, appModalTitleBarSx, appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import { META_SUBVIEWS, metaSectionSubtitle } from '../metaNavigation';

const subtitleSx = { mt: 0.5, fontSize: '13px', lineHeight: 1.4, color: 'rgba(255,255,255,0.6)' };

/*
 * Lugar en el header del modal (a la izquierda de «Cerrar») para acciones del contenido, p. ej.
 * el ⋮ «Descargar» del Informe (t990u). El contenido lo llena con un portal (`MetaDialogHeaderAction`)
 * porque el estado (si el Informe está disponible) vive adentro. Fuera del modal: null.
 */
const MetaDialogHeaderSlotContext = createContext(null);
export const useMetaDialogHeaderSlot = () => useContext(MetaDialogHeaderSlotContext);

/**
 * Análisis e Informe de la cuenta (íconos de la cabecera de Campañas) en un modal sobre el panel
 * (reunión 29/10): ya no son subvistas «CAMPAÑAS / ANÁLISIS». Mismo chrome que los modales de
 * Terrio (`appModalChrome`, ✕ arriba a la derecha); el contenido es el mismo de antes
 * (`MetaAnalysisView` / `MetaReportView`), que trae su encabezado fijo y la única zona con scroll.
 *
 * @param {{ subview: 'analysis'|'report', analysisTab?: string, onClose: () => void, children: React.ReactNode }} props
 */
export default function MetaSubviewDialog({ subview, analysisTab = null, onClose, children }) {
  const [headerSlot, setHeaderSlot] = useState(null);
  const meta = META_SUBVIEWS.find((sv) => sv.key === subview);
  if (!meta) return null;
  const titleId = `meta-${subview}-dialog-title`;
  const subtitle = metaSectionSubtitle(subview, analysisTab);
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          ...appModalPaperSx,
          width: 'min(720px, 96vw)',
          maxWidth: '720px',
          height: { xs: '92vh', sm: 'min(860px, 92vh)' },
          maxHeight: '92vh',
        },
        'data-testid': `meta-${subview}-dialog`,
      }}
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId} component="div" sx={appModalTitleBarSx}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ ...appModalTitleSx, fontSize: '18px' }}>{meta.title}</Typography>
          {subtitle ? <Typography sx={subtitleSx} data-testid={`meta-${subview}-dialog-subtitle`}>{subtitle}</Typography> : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Box ref={setHeaderSlot} sx={{ display: 'flex', alignItems: 'center', '&:empty': { display: 'none' } }} data-testid={`meta-${subview}-dialog-actions`} />
          <IconButton size="small" onClick={onClose} aria-label="Cerrar" sx={appModalCloseButtonSx}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      {/* Sin scroll acá: lo maneja la lista de la vista (encabezado fijo + lista). */}
      <DialogContent sx={{ p: 0, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <MetaDialogHeaderSlotContext.Provider value={headerSlot}>
          {children}
        </MetaDialogHeaderSlotContext.Provider>
      </DialogContent>
    </Dialog>
  );
}
